import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getProjectsForLocale } from '../data/projects';
import { categories } from '../data/products';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLocale } from '../utils/i18n';
export function Project() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    locale
  } = useLocale();
  // Locale-aware project list — slugs are stable across EN/ES, so the
  // URL still resolves correctly when the user switches languages mid-page.
  const projects = getProjectsForLocale(locale);
  const project = projects.find(p => p.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Mobile-only ordering: active project pinned to position 0 so it's
  // always the first thing the user sees in the horizontal scroller —
  // no need to scroll to find "you are here". The sm+ grid keeps the
  // canonical order for desktop browsing.
  const activeIndex = project ? projects.findIndex(p => p.slug === project.slug) : -1;
  const mobileProjects = activeIndex >= 0 ? [...projects.slice(activeIndex), ...projects.slice(0, activeIndex)] : projects;
  // Reset the mobile scroller back to the start whenever the active
  // project changes so the freshly-promoted "first" pill is actually
  // visible — otherwise the previous scrollLeft value persists and the
  // user sees the pills already partially scrolled.
  const mobileScrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mobileScrollerRef.current) {
      mobileScrollerRef.current.scrollLeft = 0;
    }
  }, [slug]);
  // SEO — set page title to the project name + intent + brand. Mirrors the
  // pattern used in pages/CityMaterial.tsx so HashRouter pages all have
  // crawlable titles. Also sets a meta description from the project tagline
  // for search-result snippets.
  useEffect(() => {
    if (!project) return;
    const originalTitle = document.title;
    document.title = `${project.name} — Materials & Pro Tips | 206Gravel`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc?.getAttribute('content') ?? '';
    if (metaDesc) {
      metaDesc.setAttribute('content', `${project.tagline} See the recommended materials, depths, and pro tips for a ${project.name.toLowerCase()} project in the Tacoma area.`);
    }
    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute('content', originalDesc);
    };
  }, [project]);
  if (!project) {
    return <Navigate to="/" replace data-id="element-3353" data-mp="85vttjo2017w" />;
  }
  const Icon = project.icon;
  // Helper to find full product details from data/products.ts
  const getProductDetails = (productSlug: string) => {
    for (const cat of categories) {
      const product = cat.products.find(p => p.slug === productSlug);
      if (product) {
        return {
          ...product,
          categoryName: cat.name
        };
      }
    }
    return null;
  };
  return <main className="min-h-screen bg-[#fdfbf7] dark:bg-[#36322F] pt-8 pb-24" data-id="element-3354" data-mp="22ogqkxzhx0pa">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3355" data-mp="22mccjn7vulba">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-3356" data-mp="1xmredf0jegzm">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3357" data-mp="2qdrng4dq6ay5">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" data-id="element-3358" data-mp="2le57hn6iigln" />
          <span className="text-stone-900 dark:text-white font-medium" data-id="element-3359" data-mp="2lfg9bhgedea4">
            {project.name}
          </span>
        </nav>

        {/* Project switcher — bold card-style tab grid. Each tab shows
            the project icon prominently with the project name as the
            primary label and typical yards as a quiet subtitle. Active
            project gets a saturated sage fill so the current context is
            unmissable; inactive tabs have a clear hover state to invite
            switching. 2 columns on mobile, 3 on sm+, 4 on md+. */}
        <div className="mb-10" data-id="element-3360" data-mp="2tmhoxjmy2jkc">
          <h2 className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-3" data-id="element-3361" data-mp="2tnssbich9pax">
            Shop by Project
          </h2>
          {/* Mobile: horizontal scrollable pill row so the project content
           stays visible below the tabs (no long vertical menu to scroll past).
           sm+: switches to a multi-column grid for desktop browsing. */}
          <div ref={mobileScrollerRef} className="flex sm:hidden gap-2 overflow-x-auto pb-2 snap-x snap-mandatory" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-3362" data-mp="3cjso748ktczs">
            <style data-id="element-3363" data-mp="32lml4jb1a0tc">{`
              .project-tabs-mobile::-webkit-scrollbar { display: none; }
            `}</style>
            {mobileProjects.map(p => {
            const PIcon = p.icon;
            const isActive = p.slug === project.slug;
            return <Link key={p.slug} to={`/projects/${p.slug}`} className={`group shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all snap-start ${isActive ? 'bg-[#04A05C] border-[#04A05C] shadow-md shadow-[#04A05C]/20' : 'bg-white dark:bg-stone-900/60 border-stone-200 dark:border-white/10'}`} aria-current={isActive ? 'page' : undefined} data-id="element-3364" data-mp="9a5t5adtqm1z">
                  <div className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-[#e8ede4] dark:bg-emerald-500/15'}`} data-id="element-3365" data-mp="e74ilfmjm4tu">
                    <PIcon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-[#3d7530] dark:text-[#9dd194]'}`} data-id="element-3366" data-mp="3hr0m9y4tks2v" />
                  </div>
                  <div className="flex flex-col" data-id="element-3367" data-mp="3vj70heoewozo">
                    <span className={`font-bold text-xs leading-tight whitespace-nowrap ${isActive ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-3368" data-mp="3qpkto34xjfbs">
                      {p.name}
                    </span>
                    <span className={`text-[10px] font-medium ${isActive ? 'text-white/80' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-3369" data-mp="2yyofmz74v4mx">
                      {p.typicalYards}
                    </span>
                  </div>
                </Link>;
          })}
          </div>

          {/* sm+ grid */}
          <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-id="element-3370" data-mp="1bpy8k0jeup2v">
            {projects.map(p => {
            const PIcon = p.icon;
            const isActive = p.slug === project.slug;
            return <Link key={p.slug} to={`/projects/${p.slug}`} className={`group relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all ${isActive ? 'bg-[#04A05C] dark:bg-[#04A05C] border-[#04A05C] dark:border-[#04A05C] shadow-md shadow-[#04A05C]/20' : 'bg-white dark:bg-stone-900/60 border-stone-200 dark:border-white/10 hover:border-[#04A05C] dark:hover:border-[#22c95c] hover:shadow-sm'}`} aria-current={isActive ? 'page' : undefined} data-id="element-3371" data-mp="j22c8wxr6z6m">
                  <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-white/20' : 'bg-[#e8ede4] dark:bg-emerald-500/15 group-hover:bg-[#d4ddcf] dark:group-hover:bg-emerald-500/25'}`} data-id="element-3372" data-mp="29bcd0sb9gq62">
                    <PIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-[#3d7530] dark:text-[#9dd194]'}`} data-id="element-3373" data-mp="3oxpeaa5uh1sv" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-3374" data-mp="3f6bh491d8e2w">
                    <div className={`font-bold text-sm leading-tight ${isActive ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-3375" data-mp="3f3fhmg9vdhcc">
                      {p.name}
                    </div>
                    <div className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-white/80' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-3376" data-mp="366y7ngd8rpi6">
                      {p.typicalYards}
                    </div>
                  </div>
                </Link>;
          })}
          </div>
        </div>

        {/* Header */}
        <div className="mb-12" data-id="element-3377" data-mp="zc1fclquik5k">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/10 text-brand-orange mb-6" data-id="element-3378" data-mp="uestob6hnxzk">
            <Icon className="w-8 h-8" data-id="element-3379" data-mp="3agtj2fonk0a" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-stone-900 dark:text-white mb-4" data-id="element-3380" data-mp="88rzh22uxad8">
            {project.name}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 font-medium mb-6" data-id="element-3381" data-mp="3pp040o1jef1h">
            {project.tagline}
          </p>
          <div className="prose prose-lg dark:prose-invert text-stone-600 dark:text-stone-400 max-w-none" data-id="element-3382" data-mp="3gp4qwdof89yt">
            <p data-id="element-3383" data-mp="3lohberrtl4y8">{project.intro}</p>
          </div>
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-stone-700 dark:text-stone-300 text-sm font-medium border border-stone-200 dark:border-white/10" data-id="element-3384" data-mp="3lp98y84z9cr5">
            <span className="mr-2" data-id="element-3385" data-mp="3brusi8j20nyu">Typical project size:</span>
            <span className="font-bold text-stone-900 dark:text-white" data-id="element-3386" data-mp="150djyu1a1ftz">
              {project.typicalYards}
            </span>
          </div>
        </div>

        {/* Materials Section */}
        <div className="mb-16" data-id="element-3387" data-mp="2ufo4wlcl9mgz">
          <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6" data-id="element-3388" data-mp="2ugpkn34jux51">
            Recommended Materials
          </h2>
          <div className="space-y-6" data-id="element-3389" data-mp="345zeooiyepvs">
            {project.materials.map((mat, idx) => {
            const product = getProductDetails(mat.slug);
            if (!product) return null;
            return <motion.div key={mat.slug} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className="bg-[#ffffff] dark:bg-[#36322F] rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-white/10 flex flex-col md:flex-row gap-6 items-start" data-id="element-3390" data-mp="3d6nplad1wa4g" data-mpi={idx}>
                  <div className="w-full md:w-48 h-32 shrink-0 rounded-xl overflow-hidden bg-stone-100 dark:bg-stone-800" data-id="element-3391" data-mp="1squath736lal">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3392" data-mp="1nt114e6kr95k" />
                  </div>
                  <div className="flex-1" data-id="element-3393" data-mp="2wluz0raeyd7">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-2" data-id="element-3394" data-mp="3u1vh2hc5ushj">
                      <div data-id="element-3395" data-mp="3p28zxwt7os3g">
                        <div className="flex items-center gap-2 mb-1" data-id="element-3396" data-mp="3p3k4vzye85w5">
                          <span className="text-xs font-bold uppercase tracking-wider text-brand-orange" data-id="element-3397" data-mp="3l170bevdue0y">
                            {mat.role}
                          </span>
                          <span className="text-stone-300 dark:text-stone-600" data-id="element-3398" data-mp="lnmqfet78bs0">
                            •
                          </span>
                          <span className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-3399" data-mp="bv7d4usukbdg">
                            Depth: {mat.depth}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 dark:text-white" data-id="element-3400" data-mp="14dkz9x4tm8wp">
                          {product.name}
                        </h3>
                      </div>
                      <div className="text-right" data-id="element-3401" data-mp="2i9mjqspuf4r6">
                        <div className="text-lg font-bold text-stone-900 dark:text-white" data-id="element-3402" data-mp="2n74lnz5g0jpo">
                          ${product.pricePerYard}
                          <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3403" data-mp="2r8832q352nt0">
                            /yd
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-stone-600 dark:text-stone-300 mb-4" data-id="element-3404" data-mp="366a7t3lwpfbu">
                      <span className="font-semibold text-stone-900 dark:text-white" data-id="element-3405" data-mp="3645szqmgyvws">
                        Why:{' '}
                      </span>
                      {mat.why}
                    </p>

                    <Link to={`/shop/${product.slug}`} className="inline-flex items-center text-sm font-bold text-brand-orange hover:text-brand-orange/80 transition-colors" data-id="element-3406" data-mp="3f0ohmqno3xnk">
                      View product details
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3407" data-mp="1ouicigmyy7v9" />
                    </Link>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-3408" data-mp="1jtchud9r0aj3">
          {/* Pro Tips */}
          <div data-id="element-3409" data-mp="1ylwf4xa7vnsm">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3410" data-mp="1ymemoq2bxofu">
              <CheckCircle2 className="w-6 h-6 mr-2 text-[#04A05C]" data-id="element-3411" data-mp="27l00y90rqhat" />
              Pro Tips
            </h2>
            <ul className="space-y-4" data-id="element-3412" data-mp="23joiqok84ycn">
              {project.tips.map((tip, idx) => <li key={idx} className="flex items-start" data-id="element-3413" data-mp="23lheh8taty5v" data-mpi={idx}>
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 mr-3" data-id="element-3414" data-mp="2hi4pgfi36ous" />
                  <span className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3415" data-mp="2cle9rta71v9w">
                    {tip}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* FAQs */}
          <div data-id="element-3416" data-mp="i7msc7lmi73w">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3417" data-mp="i86c7tuu35kk">
              <HelpCircle className="w-6 h-6 mr-2 text-brand-orange" data-id="element-3418" data-mp="2ysid168g24dm" />
              Project FAQs
            </h2>
            <div className="space-y-6" data-id="element-3419" data-mp="2k3vku3xpxi4z">
              {project.faqs.map((faq, idx) => <div key={idx} data-id="element-3420" data-mp="2k1rllzarl7b1" data-mpi={idx}>
                  <h4 className="font-bold text-stone-900 dark:text-white mb-2" data-id="element-3421" data-mp="2p26vggvu7wo9">
                    {faq.q}
                  </h4>
                  <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed" data-id="element-3422" data-mp="2b24klely63ii">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 text-center bg-[#57534e]" data-id="element-3423" data-mp="216ud4bcs53xc">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-3424" data-mp="26430qrgp605z">
            Ready to start your project?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-3425" data-mp="4c7qmga3a3pv">
            Get an instant estimate including delivery for all the materials you
            need.
          </p>
          <Link to={`/?from=project&project=${project.slug}#estimator`} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-brand-orange hover:bg-brand-orange/90 rounded-xl transition-colors" data-id="element-3426" data-mp="20d9urjnfl4yv">
            Get an Estimate
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-3427" data-mp="1mf1qeqo2f1do" />
          </Link>
        </div>
      </div>
    </main>;
}